import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../../core/design-system/components/Button';
import { Icon } from '../../../../core/design-system/components/Icon';
import { getStorage } from 'firebase/storage';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import app from '../../../../firebase';
import './PhotoStep.css';
import './common.css';
import { useAuth } from '../../../../core/providers/AuthProvider';
import { useCases } from '../../../../core/di/useCases';

const storage = getStorage(app);

const PhotoStep = ({ onComplete }) => {
  const { t } = useTranslation('onboarding');
  const { user } = useAuth();
  const [photo, setPhoto] = useState(null);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const fileInputRef = useRef(null);

  // Gérer la sélection d'une photo
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Vérifier le type et la taille du fichier
    if (!file.type.startsWith('image/')) {
      alert(t('photo.error.invalid_type'));
      return;
    }

    if (file.size > 5 * 1024 * 1024) { // 5 MB
      alert(t('photo.error.too_large'));
      return;
    }

    setPhoto(file);
    
    // Créer une URL pour prévisualiser l'image
    const url = URL.createObjectURL(file);
    setPhotoUrl(url);
  };

  // Déclencher le sélecteur de fichier natif
  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  const uploadPhoto = async (file) => {
    if (!file || !user?.uid) return null;

    const storageRef = ref(storage, `users/${user.uid}/profile-photos/${Date.now()}-${file.name}`);
    await uploadBytes(storageRef, file);
    return getDownloadURL(storageRef);
  };

  // Continuer avec photo (puisque le bouton est désactivé s'il n'y a pas de photo)
  const handleContinue = async () => {
    try {
      setIsLoading(true);
      // On est certain que photo existe puisque le bouton est désactivé sinon
      const photoURL = await uploadPhoto(photo);
      
      // Mise à jour du profil
      await useCases.updateOnboardingStep.execute(user.uid, 'photo', {
        photoURL: photoURL,
        onboardingData: {
          ...user.onboardingData,
          photoURL
        }
      });
      
      onComplete({ photoURL });
    } catch (error) {
      console.error('Erreur upload photo:', error);
      alert(t('photo.error.upload_failed'));
    } finally {
      setIsLoading(false);
    }
  };

  // Passer cette étape (optionnelle)
  const handleSkip = () => {
    onComplete({ photoURL: null });
  };

  return (
    <div className="photo-step-container">
      <div className="photo-step-content">
        <input 
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept="image/*"
          className="file-input-hidden"
          // Capturer depuis la caméra sur mobile quand possible
          capture={window.innerWidth <= 768 ? 'user' : undefined}
        />

        {photoUrl ? (
          <div className="photo-preview-container">
            <img 
              src={photoUrl} 
              alt={t('photo.preview_alt')} 
              className="photo-preview"
            />
            <button 
              className="change-photo-button"
              onClick={triggerFileInput}
              aria-label={t('photo.change')}
            >
              <Icon name="edit" size={24} />
            </button>
          </div>
        ) : (
          <div 
            className="photo-upload-placeholder"
            onClick={triggerFileInput}
          >
            <Icon name="camera" size={36} />
            <p className="photo-upload-text">{t('photo.upload_prompt')}</p>
          </div>
        )}
      </div>
      
      <div className="photo-step-actions">
        <Button 
          variant="primary"
          className="step-submit-button"
          onClick={handleContinue}
          disabled={!photo || isLoading}
        >
          {isLoading ? t('common.loading') : t('photo.continue')}
        </Button>
        
        {!photo && !isLoading && (
          <Button 
            variant="text"
            className="skip-button"
            onClick={handleSkip}
          >
            {t('photo.skip')}
          </Button>
        )}
      </div>
    </div>
  );
};

export default PhotoStep; 